import { useState, useRef, useEffect } from "react"

function formatTime(s) {
  if (!s || isNaN(s)) return "0:00"
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, "0")}`
}

/**
 * Self-contained audio player.
 * Props:
 *   src      — object URL or data URL
 *   name     — optional filename to display
 *   className
 */
export default function AudioPlayer({ src, name, className = "" }) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [seeking, setSeeking] = useState(false)
  const audioRef = useRef(null)
  const seekRef = useRef(null)
  const rafRef = useRef(null)
  const seekingRef = useRef(false)

  // Stop and reset when src changes
  useEffect(() => {
    setPlaying(false)
    setProgress(0)
    setCurrentTime(0)
    setDuration(0)
    cancelAnimationFrame(rafRef.current)
  }, [src])

  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(rafRef.current)
      return
    }
    const tick = () => {
      if (seekingRef.current || !audioRef.current) { rafRef.current = requestAnimationFrame(tick); return }
      const { currentTime: t, duration: d } = audioRef.current
      setCurrentTime(t)
      if (d) setProgress(t / d)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing])

  if (!src) return null

  const togglePlay = () => {
    if (!audioRef.current) return
    if (playing) {
      audioRef.current.pause()
      setPlaying(false)
    } else {
      audioRef.current.play()
      setPlaying(true)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration)
  }

  const seekTo = (e) => {
    const bar = seekRef.current
    if (!bar || !audioRef.current) return
    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audioRef.current.currentTime = ratio * audioRef.current.duration
    setProgress(ratio)
    setCurrentTime(ratio * audioRef.current.duration)
  }

  const handleSeekStart = (e) => { seekingRef.current = true; setSeeking(true); seekTo(e) }
  const handleSeekMove = (e) => { if (seeking) seekTo(e) }
  const handleSeekEnd = () => { seekingRef.current = false; setSeeking(false) }

  const PlayPauseIcon = () => playing ? (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
  ) : (
    <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  )

  return (
    <div className={`flex flex-col items-center gap-5 py-6 px-4 ${className}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => { setPlaying(false); setProgress(0); setCurrentTime(0) }}
      />

      {/* Icon */}
      <div className="w-16 h-16 rounded-2xl bg-admin_primary/10 flex items-center justify-center">
        <svg className="w-8 h-8 text-admin_primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
        </svg>
      </div>

      {name && (
        <p className="text-sm font-medium text-admin_text text-center truncate max-w-full px-2">{name}</p>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3 w-full">
        <button
          type="button"
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-admin_primary flex items-center justify-center text-white shadow hover:bg-admin_primary_dk transition shrink-0"
        >
          <PlayPauseIcon />
        </button>
        <div className="flex-1 flex flex-col gap-1 pt-3">
          <div
            ref={seekRef}
            className="w-full h-2 rounded-full bg-admin_neutral_2 cursor-pointer relative"
            onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleSeekStart(e) }}
            onPointerMove={handleSeekMove}
            onPointerUp={handleSeekEnd}
            onPointerCancel={handleSeekEnd}
          >
            <div className="h-full rounded-full bg-admin_primary" style={{ width: `${progress * 100}%` }} />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-admin_primary shadow"
              style={{ left: `calc(${progress * 100}% - 6px)` }}
            />
          </div>
          <div className="flex justify-between text-xs text-admin_text_secondary tabular-nums">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
